import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { SeatMapApi } from '@cineamo/api/resources/seat-map/SeatMapApi';

import { CinemaRoom, RoomObject, Seat } from '@cineamo/ui/src/seat-map/typedefs';

// CIN-11360: React binding for the framework-agnostic SeatMapApi (which lives in @cineamo/api).
export function useSeatMap(cinemaRoomId: ShowingDto['cinemaRoomId']): {
    seats: Seat[];
    /** Absent until the room query has resolved, and for good if no room id was ever passed. */
    room: CinemaRoom | undefined;
    roomObjects: RoomObject[];
    isLoading: boolean;
} {
    const seatMapApi = useRef(new SeatMapApi());

    const { data: seats = [], isLoading: isSeatsLoading } = useQuery({
        queryKey: ['cinema-room-seats', `cinemaRoomId=${cinemaRoomId}`],
        queryFn: () => seatMapApi.current.loadSeats(cinemaRoomId),
        enabled: !!cinemaRoomId
    });

    const { data: roomObjects = [], isLoading: isRoomObjectsLoading } = useQuery({
        queryKey: ['cinema-room-objects', `cinemaRoomId=${cinemaRoomId}`],
        queryFn: () => seatMapApi.current.loadRoomObjects(cinemaRoomId),
        enabled: !!cinemaRoomId
    });

    // `isPending`, not `isLoading`: a query disabled through `enabled` reports isLoading false in
    // react-query v5 even though it has no data. Callers gate their seat map on `isLoading`, so with
    // isLoading they rendered it with no room at all — which is how `data.room.depth` came to throw.
    const { data: room, isPending: isRoomPending } = useQuery({
        queryKey: ['cinema-room', `cinemaRoomId=${cinemaRoomId}`],
        queryFn: () => seatMapApi.current.loadRoom(cinemaRoomId),
        enabled: !!cinemaRoomId
    });

    const isLoading = isSeatsLoading || isRoomPending || isRoomObjectsLoading;

    return { seats, room: room ?? undefined, roomObjects, isLoading };
}
